import React, { useEffect, useState } from "react";
import AdminSidebar from "../../components/admin/AdminSidebar";
import SmartDataTable from "../../components/shared/SmartDataTable";
import { SkeletonStat } from "../../components/shared/Skeleton";
import { Search, Bell, WalletCards } from "lucide-react";
import { paymentsApi } from "../../services/api";

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "-";
}

function fmt(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
}

const LIMIT = 25;

function paymentStatusTone(status) {
  if (status === "succeeded" || status === "paid") return "live";
  if (status === "failed" || status === "refunded") return "danger";
  return "warn";
}

export default function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    paymentsApi.getStats()
      .then((res) => setStats(res.data.data || []))
      .catch((err) => setError(err.response?.data?.message || "Failed to load payment stats."))
      .finally(() => setStatsLoading(false));
  }, []);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError("");
      try {
        const res = await paymentsApi.getAll({ limit: 1000, page: 1 });
        setPayments(res.data.data || []);
        setTotal(res.data.total || 0);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load payments.");
        setPayments([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const totalRevenue = stats.reduce((sum, stat) => sum + (stat.total || 0), 0);
  const columns = [
    { name: "Date", selector: (row) => row.createdAt || "", sortable: true, cell: (row) => fmtDate(row.createdAt) },
    { name: "User", selector: (row) => row.user?.name || "", sortable: true, cell: (row) => <strong>{row.user?.name || "-"}</strong>, grow: 1.35 },
    { name: "Description", selector: (row) => row.description || "", sortable: true, cell: (row) => <span className="table-muted">{row.description || "-"}</span>, grow: 1.6 },
    { name: "Type", selector: (row) => row.type || "", sortable: true, cell: (row) => <span style={{ textTransform: "capitalize" }}>{row.type?.replace("_", " ") || "-"}</span> },
    { name: "Amount", selector: (row) => row.amount || 0, sortable: true, cell: (row) => <b className="table-primary">{fmt(row.amount)}</b> },
    { name: "Status", selector: (row) => row.status || "", sortable: true, cell: (row) => <span className={`table-status ${paymentStatusTone(row.status)}`}>{row.status}</span> },
  ];

  return (
    <div className="admin-shell">
      <AdminSidebar />
      <main className="admin-main">
        <header className="admin-topbar">
          <div className="admin-title">Payments</div>
          <label className="admin-search"><Search size={15} /><input placeholder="Search payments..." /></label>
          <div className="system-live"><i /> System Live</div>
          <button className="icon-button" aria-label="Notifications"><Bell size={18} /><i /></button>
        </header>

        <div className="admin-content">
          {error && <div className="auth-error" style={{ marginBottom: 16 }}>{error}</div>}
          <section className="stats-grid" style={{ gridTemplateColumns: "repeat(3, minmax(0,1fr))" }}>
            {statsLoading ? (
              Array.from({ length: 3 }).map((_, i) => <SkeletonStat key={i} />)
            ) : (
              <>
                <article className="stat-card amber">
                  <span><WalletCards size={21} /></span>
                  <div><small>Total Revenue</small><strong>{fmt(totalRevenue)}</strong></div>
                </article>
                {stats.map((stat) => (
                  <article key={stat._id} className="stat-card blue">
                    <span><WalletCards size={21} /></span>
                    <div>
                      <small>{stat._id?.replace("_", " ") || "Payments"}</small>
                      <strong>{fmt(stat.total)} <span style={{ fontSize: 13, fontWeight: 400, color: "var(--muted)" }}>({stat.count})</span></strong>
                    </div>
                  </article>
                ))}
              </>
            )}
          </section>

          <section className="admin-panel" style={{ padding: 0 }}>
            <div className="panel-heading">
              <h2><WalletCards size={18} /> All Transactions
                <span className="muted-count" style={{ marginLeft: 8 }}>{total} records</span>
              </h2>
            </div>
            <SmartDataTable
              columns={columns}
              data={payments}
              loading={loading}
              searchFields={["user.name", "user.email", "description", "type", "status", "pool.poolId"]}
              searchPlaceholder="Search transactions by user, type, status..."
              emptyMessage="No payments yet."
              paginationPerPage={LIMIT}
            />
          </section>
        </div>
      </main>
    </div>
  );
}
